import React, { useEffect, useState } from 'react';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Grid,
  TextField,
  Container,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Stack,
  Typography,
  Divider,
  Chip,
  Tooltip,
} from '@mui/material';
import Auto_Load from './Auto_Load';
import 'react-toastify/dist/ReactToastify.css';
import { useNotification } from './context/NotificationContext';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#0c0d12',
      paper: '#14171f',
    },
    text: {
      primary: '#e8e9ee',
      secondary: 'rgba(232,233,238,0.7)',
    },
    primary: {
      main: '#6aa9ff',
    },
    success: {
      main: '#5ad27a',
    },
    error: {
      main: '#ff6b6b',
    },
  },
  typography: {
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
});

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.02)',
    color: '#e8e9ee',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
    '&.Mui-focused fieldset': { borderColor: '#6aa9ff' },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(232,233,238,0.7)',
  },
};

const panelSx = {
  borderRadius: 3,
  background: 'rgba(18, 18, 22, 0.75)',
  border: '1px solid rgba(255,255,255,0.06)',
};

function getDefaultManualExpiryDate() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
}

function getDefaultManualExpiryTime() {
  return '23:59';
}

function NumberLoader() {
  const { showNotification } = useNotification();
  const FX_CACHE_KEY = 'usd_php_rate_cache_v1';
  const [numbers, setNumbers] = useState('');
  const [manualSuccessNumbers, setManualSuccessNumbers] = useState('');
  const [manualExpiryDate, setManualExpiryDate] = useState(getDefaultManualExpiryDate);
  const [manualExpiryTime, setManualExpiryTime] = useState(getDefaultManualExpiryTime);
  const [manualSuccessSubmitting, setManualSuccessSubmitting] = useState(false);
  const [manualSuccessResults, setManualSuccessResults] = useState(null);
  const [expiredNumbers, setExpiredNumbers] = useState({});
  const [balances, setBalances] = useState({});
  const [usdToPhpRate, setUsdToPhpRate] = useState(null);
  const [validityDialogOpen, setValidityDialogOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [selectedValidity, setSelectedValidity] = useState('');
  const [selectedValidityUrl, setSelectedValidityUrl] = useState(null);
  const [confirmationCallback, setConfirmationCallback] = useState(null);
  const [dialogContext, setDialogContext] = useState('');

  useEffect(() => {
    document.title = 'Loader';

    const fetchExpiredNumbers = () => {
      fetch('https://frek.cc/proxy/38.54.86.24:3322/expired-numbers')
        .then((response) => response.json())
        .then((data) => setExpiredNumbers(data || {}))
        .catch((error) => console.error('Error fetching expired numbers:', error));
    };

    const fetchBalances = () => {
      fetch('https://frek.cc/proxy/38.54.86.24:7700/get_balances')
        .then((response) => response.json())
        .then((data) => setBalances(data || {}))
        .catch((error) => console.error('Error fetching balances:', error));
    };

    const getTodayKey = () => new Date().toISOString().slice(0, 10);

    const fetchUsdPhpRate = async () => {
      try {
        const cachedRaw = localStorage.getItem(FX_CACHE_KEY);
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw);
          if (cached?.date === getTodayKey() && typeof cached?.rate === 'number' && cached.rate > 0) {
            setUsdToPhpRate(cached.rate);
            return;
          }
        }
      } catch (error) {
        console.error('Error reading FX cache:', error);
      }

      try {
        const primary = await fetch('https://open.er-api.com/v6/latest/USD');
        const primaryData = await primary.json();
        const primaryRate = primaryData?.rates?.PHP;
        if (typeof primaryRate === 'number' && primaryRate > 0) {
          setUsdToPhpRate(primaryRate);
          localStorage.setItem(FX_CACHE_KEY, JSON.stringify({ date: getTodayKey(), rate: primaryRate }));
          return;
        }
      } catch (error) {
        console.error('Error fetching FX rate from primary source:', error);
      }

      try {
        const fallback = await fetch('https://api.frankfurter.app/latest?from=USD&to=PHP');
        const fallbackData = await fallback.json();
        const fallbackRate = fallbackData?.rates?.PHP;
        if (typeof fallbackRate === 'number' && fallbackRate > 0) {
          setUsdToPhpRate(fallbackRate);
          localStorage.setItem(FX_CACHE_KEY, JSON.stringify({ date: getTodayKey(), rate: fallbackRate }));
        }
      } catch (error) {
        console.error('Error fetching FX rate from fallback source:', error);
      }
    };

    fetchExpiredNumbers();
    fetchBalances();
    fetchUsdPhpRate();

    const intervalIdNumbers = setInterval(fetchExpiredNumbers, 5000);
    const intervalIdBalances = setInterval(fetchBalances, 5000);

    return () => {
      clearInterval(intervalIdNumbers);
      clearInterval(intervalIdBalances);
    };
  }, []);

  const openValidityDialog = (context, callback) => {
    setDialogContext(context);
    setConfirmationCallback(() => callback);
    setValidityDialogOpen(true);
  };

  const handleValiditySelection = (validity) => {
    setValidityDialogOpen(false);
    let url = null;
    if (validity === '2') {
      url = 'https://frek.cc/proxy/38.54.86.24:7700/add_numbers';
    } else if (validity === '7') {
      url = 'https://frek.cc/proxy/38.54.86.24:7700/add_numbers_7d';
    }
    if (url) {
      setSelectedValidity(validity);
      setSelectedValidityUrl(url);
      setConfirmationDialogOpen(true);
    } else {
      handleNotification('Invalid selection. Loading cancelled.', 'error');
    }
  };

  const handleConfirmation = () => {
    if (confirmationText.trim().toUpperCase() === 'CONFIRM' && confirmationCallback) {
      setConfirmationDialogOpen(false);
      setConfirmationText('');
      confirmationCallback(selectedValidityUrl);
    } else {
      handleNotification('Invalid confirmation text. Loading cancelled.', 'error');
    }
  };

  const loadExpiredNumbers = () => {
    openValidityDialog('Expired Numbers', (url) => {
      const allNumbers = Object.values(expiredNumbers).flat().join(', ');
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: allNumbers,
      })
        .then((response) => response.text())
        .then((data) => handleNotification(`Response: ${data}`))
        .catch(() => handleNotification('Error Loading Expired Numbers!', 'error'));
    });
  };

  const fetchAndSubmitNumbers = (groupNumber) => {
    const url = `https://frek.cc/proxy/38.54.86.24:49152/fetch_numbers/${groupNumber}`;

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const fetchedNumbers = data.filter((num) => num).join(', ');
        openValidityDialog(`Goip ${groupNumber}`, (validityUrl) => {
          fetch(validityUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'text/plain',
            },
            body: fetchedNumbers,
          })
            .then((response) => response.text())
            .then((responseData) => handleNotification(`Response: ${responseData}`))
            .catch((error) => console.error('Error submitting numbers:', error));
        });
      })
      .catch((error) => console.error('Error fetching numbers:', error));
  };

  const submitNumbers = (fetchedNumbers, isManual = false) => {
    openValidityDialog('Manual Loading', (validityUrl) => {
      fetch(validityUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: isManual ? numbers : fetchedNumbers,
      })
        .then((response) => response.text())
        .then((data) => handleNotification(`Response: ${data}`))
        .catch((error) => console.error('Error submitting numbers:', error));
    });
  };

  const handleNotification = (message, severity = 'success') => {
    showNotification(message, severity);
  };

  const submitManualSuccess2d = async () => {
    const parsedNumbers = manualSuccessNumbers
      .split(/[\n,]+/)
      .map((value) => value.trim())
      .filter(Boolean);

    if (!parsedNumbers.length) {
      handleNotification('Enter at least one number for manual 2-day success.', 'error');
      return;
    }

    if (!manualExpiryDate || !manualExpiryTime) {
      handleNotification('Select both expiry date and time.', 'error');
      return;
    }

    setManualSuccessSubmitting(true);
    try {
      const response = await fetch('https://frek.cc/proxy/38.54.86.24:7700/manual_load_success_2d', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          numbers: parsedNumbers,
          expiry: `${manualExpiryDate} ${manualExpiryTime}`,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Manual 2-day success request failed.');
      }

      setManualSuccessResults(data);
      handleNotification(
        `Processed ${data.processed} numbers. Success: ${data.successful}, Failed: ${data.failed}.`
      );
    } catch (error) {
      handleNotification(error.message || 'Manual 2-day success request failed.', 'error');
    } finally {
      setManualSuccessSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'radial-gradient(1200px 600px at 15% -10%, rgba(78, 118, 255, 0.28), transparent 60%),' +
            'radial-gradient(900px 600px at 110% 10%, rgba(0, 200, 170, 0.22), transparent 55%),' +
            'linear-gradient(180deg, #0b0b10 0%, #101018 45%, #0d0f12 100%)',
          color: '#e8e9ee',
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                ...panelSx,
                p: 2,
                backdropFilter: 'blur(6px)',
              }}
            >
              <Stack spacing={1.2}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      Loader
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
                      Batch and manual number loading with expiry and balance tracking.
                    </Typography>
                  </Box>
                </Box>

                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label={`Accounts ${Object.keys(balances).length}`}
                    sx={{ bgcolor: 'rgba(106,169,255,0.2)', color: '#d5e7ff', fontWeight: 700 }}
                  />
                  <Chip
                    label={`Goips ${Object.keys(expiredNumbers).length}`}
                    sx={{ bgcolor: 'rgba(255,107,107,0.18)', color: '#ffb3b3', fontWeight: 700 }}
                  />
                </Stack>
              </Stack>
            </Paper>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1.25, color: '#d9e6ff' }}>Batch Loading for each GOIP</Typography>
              <Grid container spacing={1.25} justifyContent="center">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((groupNumber) => (
                  <Grid item xs={6} sm={3} md={2} lg={1.5} key={groupNumber}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => fetchAndSubmitNumbers(groupNumber)}
                    >
                      Load G{groupNumber}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1.25, color: '#d9e6ff' }}>Manual Loading of Numbers</Typography>
              <TextField
                label="Numbers"
                multiline
                rows={4}
                fullWidth
                margin="normal"
                variant="outlined"
                value={numbers}
                onChange={(e) => setNumbers(e.target.value)}
                placeholder="Enter numbers separated by commas or new lines"
                sx={fieldSx}
              />
              <Button variant="contained" color="success" onClick={() => submitNumbers(null, true)} sx={{ mt: 1 }}>
                Load Numbers
              </Button>
            </Paper>

            <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#d9e6ff' }}>
                    Manual 2-Day Success Tool
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, mt: 0.4 }}>
                    Sets expiry, increments Mongo 2-day load stats, and calls the external `load2` counter.
                  </Typography>
                </Box>

                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Numbers"
                      multiline
                      rows={5}
                      fullWidth
                      value={manualSuccessNumbers}
                      onChange={(e) => setManualSuccessNumbers(e.target.value)}
                      placeholder="Enter numbers separated by commas or new lines"
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack spacing={1.25}>
                      <TextField
                        label="Expiry Date"
                        type="date"
                        fullWidth
                        value={manualExpiryDate}
                        onChange={(e) => setManualExpiryDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={fieldSx}
                      />
                      <TextField
                        label="Expiry Time"
                        type="time"
                        fullWidth
                        value={manualExpiryTime}
                        onChange={(e) => setManualExpiryTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={fieldSx}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={submitManualSuccess2d}
                        disabled={manualSuccessSubmitting}
                      >
                        {manualSuccessSubmitting ? 'Submitting...' : 'Submit 2-Day Success'}
                      </Button>
                      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                        The selected date and time are sent to the backend as the exact expiry.
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>

                {manualSuccessResults ? (
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      <Chip
                        label={`Processed ${manualSuccessResults.processed || 0}`}
                        sx={{ bgcolor: 'rgba(106,169,255,0.2)', color: '#d5e7ff', fontWeight: 700 }}
                      />
                      <Chip
                        label={`Success ${manualSuccessResults.successful || 0}`}
                        sx={{ bgcolor: 'rgba(90,210,122,0.18)', color: '#baf5c9', fontWeight: 700 }}
                      />
                      <Chip
                        label={`Failed ${manualSuccessResults.failed || 0}`}
                        sx={{ bgcolor: 'rgba(255,107,107,0.18)', color: '#ffb3b3', fontWeight: 700 }}
                      />
                      <Chip
                        label={`Expiry ${manualSuccessResults.expiry_value || '-'}`}
                        sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: '#f1f3f8', fontWeight: 700 }}
                      />
                    </Stack>

                    <TableContainer component={Paper} sx={{ background: 'rgba(13,14,18,0.8)' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Number</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Expiry</TableCell>
                            <TableCell>Mongo</TableCell>
                            <TableCell>Load2</TableCell>
                            <TableCell>Message</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(manualSuccessResults.results || []).map((result) => (
                            <TableRow key={`${result.number}-${result.expiry_value || 'manual'}`}>
                              <TableCell>{result.number}</TableCell>
                              <TableCell>
                                {result.success ? (
                                  <Chip size="small" label="SUCCESS" color="success" />
                                ) : (
                                  <Chip size="small" label="FAILED" color="error" />
                                )}
                              </TableCell>
                              <TableCell>{result.expiry_value || '-'}</TableCell>
                              <TableCell>{result.mongo_incremented ? 'Yes' : 'No'}</TableCell>
                              <TableCell>{result.load2_notified ? 'Yes' : 'No'}</TableCell>
                              <TableCell>{result.message || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                ) : null}
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ ...panelSx, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.3, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography sx={{ fontWeight: 700, color: '#d9e6ff' }}>Account Balances</Typography>
              </Box>
              <TableContainer>
                <Table size="small" aria-label="balances table">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: '#d9e6ff',
                          backgroundColor: 'rgba(30, 35, 52, 0.92)',
                          borderBottom: '1px solid rgba(255,255,255,0.12)',
                        }}
                        align="center"
                      >
                        Account Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: '#d9e6ff',
                          backgroundColor: 'rgba(30, 35, 52, 0.92)',
                          borderBottom: '1px solid rgba(255,255,255,0.12)',
                        }}
                        align="center"
                      >
                        Balance
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(balances).map(([accountName, balance]) => (
                      <TableRow key={accountName} sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
                        <TableCell align="center">{accountName}</TableCell>
                        <TableCell align="center">
                          {`USD ${Number(balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          {usdToPhpRate
                            ? ` / PHP ${(Number(balance) * usdToPhpRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : ' / PHP ...'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            <Paper elevation={0} sx={{ ...panelSx, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.3, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <Typography sx={{ fontWeight: 700, color: '#d9e6ff' }}>Expired Numbers</Typography>
              </Box>
              <TableContainer>
                <Table size="small" aria-label="expired numbers table">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: '#d9e6ff',
                          backgroundColor: 'rgba(30, 35, 52, 0.92)',
                          borderBottom: '1px solid rgba(255,255,255,0.12)',
                        }}
                        align="center"
                      >
                        GOIP Device
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: '#d9e6ff',
                          backgroundColor: 'rgba(30, 35, 52, 0.92)',
                          borderBottom: '1px solid rgba(255,255,255,0.12)',
                        }}
                        align="center"
                      >
                        Expired Numbers
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(expiredNumbers).map(([goipNumber, goipNumbers]) => (
                      <TableRow key={goipNumber} sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)' }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
                        <TableCell align="center">Goip {goipNumber}</TableCell>
                        <TableCell align="center">{goipNumbers.join(', ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ p: 1.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <Tooltip title="Load all expired numbers after selecting validity">
                  <Button variant="contained" onClick={loadExpiredNumbers}>
                    Load Expired Numbers
                  </Button>
                </Tooltip>
              </Box>
            </Paper>

            <Auto_Load />
          </Stack>
        </Container>

        <Dialog
          open={validityDialogOpen}
          onClose={() => setValidityDialogOpen(false)}
          PaperProps={{
            sx: {
              background: 'rgba(18, 18, 22, 0.96)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#e8e9ee',
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle>Select Load Validity</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Please select the Load validity for the numbers ({dialogContext}): 2 days or 7 days.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => handleValiditySelection('2')} variant="contained">2 Days</Button>
            <Button onClick={() => handleValiditySelection('7')} variant="contained" color="success">7 Days</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmationDialogOpen}
          onClose={() => setConfirmationDialogOpen(false)}
          PaperProps={{
            sx: {
              background: 'rgba(18, 18, 22, 0.96)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#e8e9ee',
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle>Confirmation</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Type the word "CONFIRM" to proceed loading {selectedValidity} days for {dialogContext}.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Confirmation Text"
              type="text"
              fullWidth
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              sx={fieldSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmationDialogOpen(false)} color="error">Cancel</Button>
            <Button onClick={handleConfirmation} variant="contained">Confirm</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}

export default NumberLoader;
